import React, { useState, useEffect } from 'react';
import { riderAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CreditCard, Plus, Trash2, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const PaymentMethods = () => {
  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCard, setNewCard] = useState({
    card_type: 'credit',
    last_four: '',
    expiry_month: '',
    expiry_year: '',
    is_default: false,
  });

  useEffect(() => {
    loadMethods();
  }, []);

  const loadMethods = async () => {
    try {
      const response = await riderAPI.getPaymentMethods();
      setMethods(response.data);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async () => {
    if (!newCard.last_four || newCard.last_four.length !== 4) {
      toast.error('Please enter last 4 digits of card');
      return;
    }
    if (!newCard.expiry_month || !newCard.expiry_year) {
      toast.error('Please enter expiry date');
      return;
    }

    setSaving(true);
    try {
      await riderAPI.addPaymentMethod({
        ...newCard,
        expiry_month: parseInt(newCard.expiry_month),
        expiry_year: parseInt(newCard.expiry_year),
      });
      toast.success('Payment method added');
      setDialogOpen(false);
      setNewCard({
        card_type: 'credit',
        last_four: '',
        expiry_month: '',
        expiry_year: '',
        is_default: false,
      });
      loadMethods();
    } catch (error) {
      toast.error('Failed to add payment method');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (methodId) => {
    try {
      await riderAPI.deletePaymentMethod(methodId);
      toast.success('Payment method removed');
      loadMethods();
    } catch (error) {
      toast.error('Failed to remove payment method');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1).padStart(2, '0'),
  }));

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => ({
    value: String(currentYear + i),
    label: String(currentYear + i),
  }));

  return (
    <div className="p-6" data-testid="payment-methods-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight">
            Payment Methods
          </h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-red-600 hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Card
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display uppercase">Add Payment Method</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Card Type</Label>
                  <Select
                    value={newCard.card_type}
                    onValueChange={(v) => setNewCard({ ...newCard, card_type: v })}
                  >
                    <SelectTrigger data-testid="card-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit">Credit Card</SelectItem>
                      <SelectItem value="debit">Debit Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Last 4 Digits</Label>
                  <Input
                    value={newCard.last_four}
                    onChange={(e) => setNewCard({ ...newCard, last_four: e.target.value.slice(0, 4) })}
                    placeholder="1234"
                    maxLength={4}
                    className="h-12"
                    data-testid="card-last-four-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expiry Month</Label>
                    <Select
                      value={newCard.expiry_month}
                      onValueChange={(v) => setNewCard({ ...newCard, expiry_month: v })}
                    >
                      <SelectTrigger data-testid="expiry-month-select">
                        <SelectValue placeholder="MM" />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Year</Label>
                    <Select
                      value={newCard.expiry_year}
                      onValueChange={(v) => setNewCard({ ...newCard, expiry_year: v })}
                    >
                      <SelectTrigger data-testid="expiry-year-select">
                        <SelectValue placeholder="YYYY" />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((y) => (
                          <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={newCard.is_default}
                    onChange={(e) => setNewCard({ ...newCard, is_default: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="is_default" className="text-sm font-normal cursor-pointer">
                    Set as default payment method
                  </Label>
                </div>

                <Button
                  onClick={handleAddCard}
                  disabled={saving}
                  className="w-full rounded-full bg-red-600 hover:bg-red-700 font-bold"
                  data-testid="save-card-btn"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Add Card'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {methods.length === 0 ? (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                No payment methods
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                Add a credit or debit card to pay for rides
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {methods.map((method) => (
              <Card 
                key={method.id} 
                className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-10 bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                          •••• •••• •••• {method.last_four}
                          {method.is_default && (
                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">
                              Default
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 capitalize">
                          {method.card_type} · Expires {method.expiry_month}/{method.expiry_year}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(method.id)}
                      className="text-slate-400 hover:text-red-600"
                      data-testid={`delete-card-${method.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-8 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            <CheckCircle className="w-4 h-4 inline mr-1 text-green-600" />
            <strong>Secure Payments:</strong> All payments are processed securely through Stripe. 
            We never store your full card details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;
